import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Radar } from 'lucide-react';
import { COMPETITORS } from '@/constants/testIds';
import { toast } from 'sonner';

export default function Competitors() {
  const { activeClient } = useClient();
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeClient) return;
    api.get(`/clients/${activeClient.client_id}/competitors/latest`).then(r => setLatest(r.data || null));
  }, [activeClient]);

  if (!activeClient) return <div className="text-sm text-muted-foreground">Select a client.</div>;

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/competitors/scan', { client_id: activeClient.client_id });
      setLatest(data);
      toast.success('Competitor scan complete');
    } catch (e) {
      toast.error('Scan failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Command center</div>
          <h1 className="mt-2 font-display text-3xl tracking-tight">Competitor intelligence</h1>
        </div>
        <Button data-testid={COMPETITORS.scanBtn} onClick={scan} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Radar className="h-4 w-4 mr-1" />}
          {loading ? 'Scanning…' : 'Run scan'}
        </Button>
      </div>

      {!latest && (
        <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <div className="text-sm text-muted-foreground">No scans yet. Add competitor URLs in onboarding, then run a scan.</div>
        </div>
      )}

      {latest && latest.analysis && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Deep Reasoning verdict</div>
          <p className="mt-2 text-sm leading-relaxed">{latest.analysis.verdict}</p>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <Block title="Threats" items={latest.analysis.threats} />
            <Block title="Opportunities" items={latest.analysis.opportunities} />
            <Block title="Recommended moves" items={latest.analysis.recommended_moves} />
          </div>
        </div>
      )}

      {latest && (latest.scanned || []).length > 0 && (
        <div data-testid={COMPETITORS.resultsList} className="grid md:grid-cols-2 gap-4">
          {(latest.scanned || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="font-medium truncate">{s.domain}</div>
                <Badge variant={s.status === 200 ? 'default' : 'destructive'}>{s.status}</Badge>
              </div>
              <div className="mt-2 text-sm font-medium truncate">{s.title || '—'}</div>
              <div className="mt-1 text-xs text-muted-foreground line-clamp-3">{s.meta_description || s.summary}</div>
              <div className="mt-4 grid grid-cols-4 gap-2 text-center">
                <Stat label="H1s" value={s.h1_count} />
                <Stat label="Words" value={s.word_count} />
                <Stat label="Links" value={s.links_count} />
                <Stat label="Schema" value={s.has_schema ? 'Yes' : 'No'} />
              </div>
            </div>
          ))}
        </div>
      )}

      {latest && latest.analysis && (latest.analysis.rankings || []).length > 0 && (
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Authority rankings</div>
          <div className="space-y-2">
            {latest.analysis.rankings.map((r, i) => (
              <div key={i} className="flex items-center gap-4 rounded-md border border-border p-3">
                <div className="score-num text-2xl text-primary w-12">{r.authority_estimate}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">↑ {r.key_advantage} · ↓ {r.key_weakness}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
      <ul className="text-sm space-y-1">
        {(items || []).map((it, i) => <li key={i} className="flex gap-2"><span className="text-primary">→</span><span>{it}</span></li>)}
      </ul>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-secondary/50 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
